import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, ArrowsClockwise, TreeStructure, Warning } from "@phosphor-icons/react";
import { Header } from "./shared";
import { PlayoffBracketView } from "@/components/PlayoffBracketView";
import { PlayoffMatchModal } from "./PlayoffMatchModal";

const DIVS = [
  { key: "Open", label: "Open" },
  { key: "35 Over", label: "35+" },
  { key: "45 Over", label: "45+" },
];

export default function PlayoffsTab() {
  const [division, setDivision] = useState("Open");
  const [bracket, setBracket] = useState(null);
  const [pool, setPool] = useState([]);
  const [size, setSize] = useState(4);
  const [seeds, setSeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editMatch, setEditMatch] = useState(null);

  const loadBracket = useCallback(() => {
    setLoading(true);
    api.get(`/playoffs/${encodeURIComponent(division)}`)
      .then((r) => setBracket(r.data.bracket))
      .catch(() => setBracket(null))
      .finally(() => setLoading(false));
  }, [division]);

  const loadPool = useCallback(() => {
    api.get(`/playoffs/${encodeURIComponent(division)}/seeds`).then((r) => setPool(r.data || [])).catch(() => setPool([]));
  }, [division]);

  useEffect(() => { loadBracket(); loadPool(); }, [loadBracket, loadPool]);

  // Initialise the seed list from standings order whenever pool/size changes and there is no bracket.
  useEffect(() => {
    if (bracket) return;
    const maxSize = Math.min(16, pool.length);
    const s = Math.max(4, Math.min(size, maxSize || 4));
    if (s !== size) setSize(s);
    setSeeds(Array.from({ length: s }, (_, i) => pool[i]?.team_id || ""));
  }, [pool, size, bracket]); // eslint-disable-line

  const autoSeed = () => setSeeds(Array.from({ length: size }, (_, i) => pool[i]?.team_id || ""));

  const setSeedAt = (i, tid) => setSeeds((prev) => prev.map((x, idx) => (idx === i ? tid : x)));

  const dupIds = seeds.filter((v, i) => v && seeds.indexOf(v) !== i);

  const generate = async () => {
    if (seeds.some((s) => !s)) { toast.error("Assign a team to every seed"); return; }
    if (dupIds.length) { toast.error("Each team can only be seeded once"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/playoffs/${encodeURIComponent(division)}/generate`, { size, seeds });
      setBracket(r.data);
      toast.success(`${DIVS.find((d) => d.key === division)?.label} bracket generated`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetBracket = async () => {
    if (!window.confirm("Reset this division's playoff bracket? All entered playoff scores will be cleared.")) return;
    setBusy(true);
    try {
      await api.delete(`/playoffs/${encodeURIComponent(division)}`);
      setBracket(null);
      loadPool();
      toast.success("Bracket reset");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="playoffs-tab">
      <Header title="Playoffs" action={bracket && (
        <button onClick={resetBracket} disabled={busy} data-testid="playoffs-reset-btn" className="flex items-center gap-2 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition-all duration-200">
          <ArrowsClockwise size={16} /> Reset / Regenerate
        </button>
      )} />

      <div className="flex flex-wrap gap-2 mb-8" data-testid="playoffs-admin-tabs">
        {DIVS.map((d) => (
          <button key={d.key} onClick={() => setDivision(d.key)} data-testid={`playoffs-admin-tab-${d.key.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${division === d.key ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"}`}>
            {d.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-white/40">Loading…</p>
      ) : bracket ? (
        <>
          <p className="text-white/40 text-xs mb-4">Tap any matchup to set its date, time & court, send it to the Scorekeeper, enter a result, or fix the teams.</p>
          <PlayoffBracketView bracket={bracket} onEditMatch={setEditMatch} />
        </>
      ) : (
        <SetupPanel pool={pool} size={size} setSize={setSize} seeds={seeds} setSeedAt={setSeedAt}
          autoSeed={autoSeed} generate={generate} busy={busy} dupIds={dupIds} />
      )}

      {editMatch && (
        <PlayoffMatchModal
          division={division}
          match={editMatch}
          bracket={bracket}
          onClose={() => setEditMatch(null)}
          onSaved={(b) => { setBracket(b); setEditMatch(null); }}
        />
      )}
    </div>
  );
}

function SetupPanel({ pool, size, setSize, seeds, setSeedAt, autoSeed, generate, busy, dupIds }) {
  const maxSize = Math.min(16, pool.length);
  if (pool.length < 4) {
    return (
      <div className="border-2 border-dashed border-white/15 py-16 text-center" data-testid="playoffs-need-teams">
        <TreeStructure size={40} className="text-white/25 mx-auto mb-4" />
        <p className="text-white/50">This division needs at least 4 teams before you can build a playoff bracket. It currently has {pool.length}.</p>
      </div>
    );
  }
  return (
    <div className="max-w-2xl border border-white/10 bg-[#121212] p-6" data-testid="playoffs-setup">
      <div className="flex items-center gap-2 mb-2"><Trophy size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Build Bracket</h3></div>
      <p className="text-white/40 text-xs mb-5">Choose how many teams make the playoffs (4–{maxSize}). Seeds are pre-filled from the current standings — adjust any of them, then generate. Byes are added automatically for the top seeds when needed.</p>

      <div className="flex items-end gap-3 mb-5 flex-wrap">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Number of teams</label>
          <select value={size} onChange={(e) => setSize(parseInt(e.target.value, 10))} data-testid="playoffs-size-select" className="bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722]">
            {Array.from({ length: maxSize - 3 }, (_, i) => i + 4).map((n) => <option key={n} value={n}>{n} teams</option>)}
          </select>
        </div>
        <button onClick={autoSeed} data-testid="playoffs-autoseed-btn" className="flex items-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 text-xs font-bold uppercase tracking-wide px-4 py-2.5 transition-all duration-200">
          <ArrowsClockwise size={14} /> Reset seeds from standings
        </button>
      </div>

      <div className="space-y-2 mb-5" data-testid="playoffs-seed-editor">
        {seeds.map((tid, i) => {
          const isDup = tid && dupIds.includes(tid);
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="font-display text-lg text-[#FF5722] tabular-nums w-8 text-center">#{i + 1}</span>
              <select value={tid} onChange={(e) => setSeedAt(i, e.target.value)} data-testid={`playoffs-seed-${i + 1}`}
                className={`flex-1 bg-[#1A1A1A] border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] ${isDup ? "border-[#FF3B30]" : "border-white/15"}`}>
                <option value="">— Select team —</option>
                {pool.map((t) => <option key={t.team_id} value={t.team_id}>{`${t.team_name} (${t.w}-${t.l})`}</option>)}
              </select>
            </div>
          );
        })}
      </div>

      {dupIds.length > 0 && (
        <p className="flex items-center gap-2 text-[#FF3B30] text-xs mb-4" data-testid="playoffs-dup-warning"><Warning size={14} weight="fill" /> A team is seeded more than once — fix the highlighted rows.</p>
      )}

      <button onClick={generate} disabled={busy} data-testid="playoffs-generate-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
        {busy ? "Generating…" : "Generate Bracket"}
      </button>
    </div>
  );
}
